import upperFirst from 'lodash/upperFirst'
import React, { useMemo } from 'react'

import type {
  LiveQueryDestination,
  LiveQueryDestinationKind,
  LiveQuerySinkConfig,
} from '../../../types/dataBridge'
import {
  isKafkaDestination,
  isKafkaSinkConfig,
  isWebhookDestination,
  isWebhookSinkConfig,
} from '../../../types/dataBridge'
import DataBridgeKeyValue from '../components/DataBridgeKeyValue'
import DataBridgeSectionCard from '../components/DataBridgeSectionCard'
import IndeterminateDestination from './IndeterminateDestination'
import KafkaDestination from './KafkaDestination'
import WebhookDestination from './WebhookDestination'

type Props = {
  /**
   * The destination configuration
   */
  destination: LiveQueryDestination | null
  /**
   * The destination kind (kafka or webhook)
   */
  destinationKind: LiveQueryDestinationKind | null
  /**
   * The sink configuration (credentials, endpoints, etc.)
   */
  configuration: LiveQuerySinkConfig | null
  /**
   * Whether the component is in a loading state
   */
  isLoading?: boolean
  /**
   * Callback when destination type cannot be determined (invalid state)
   */
  onInvalidDestination?: (error: { destinationKind?: string | null }) => void
  /**
   * Callback when certificate extraction fails (Kafka only)
   */
  onCertExtractionError?: (error: Error) => void
}

export default function DestinationSectionContent({
  destination,
  destinationKind,
  configuration,
  isLoading = false,
  onInvalidDestination,
  onCertExtractionError,
}: Props) {
  const isKnownDestination = useMemo(
    () => isKafkaDestination(destination) || isWebhookDestination(destination),
    [destination],
  )
  const isInvalidState = useMemo(
    () => !isLoading && destination !== null && !isKnownDestination,
    [isLoading, destination, isKnownDestination],
  )

  // Notify parent of invalid state
  useMemo(() => {
    if (isInvalidState && onInvalidDestination) {
      onInvalidDestination({ destinationKind })
    }
  }, [isInvalidState, onInvalidDestination, destinationKind])

  const renderDestinationDetails = () => {
    const isWebhookConfig = !configuration || isWebhookSinkConfig(configuration)
    const isKakfaConfig = !configuration || isKafkaSinkConfig(configuration)

    if (isWebhookDestination(destination) && isWebhookConfig) {
      return (
        <WebhookDestination destination={destination} config={configuration} />
      )
    } else if (isKafkaDestination(destination) && isKakfaConfig) {
      return (
        <KafkaDestination
          destination={destination}
          configuration={configuration}
          onCertExtractionError={onCertExtractionError}
        />
      )
    } else {
      if (destination && onInvalidDestination) {
        onInvalidDestination({ destinationKind })
      }

      return <IndeterminateDestination />
    }
  }

  const getSectionTitle = () => {
    if (isKafkaDestination(destination)) {
      return 'Kafka consumer info'
    } else if (isWebhookDestination(destination)) {
      return 'Webhook details'
    } else {
      return 'Destination'
    }
  }

  const getSectionDescription = () => {
    if (isKafkaDestination(destination)) {
      return 'Connection info for hosted Kafka  '
    } else if (isWebhookDestination(destination)) {
      return 'Details of configured webhook'
    } else {
      return 'The destination receiving your data'
    }
  }

  if (isInvalidState) {
    return null
  }

  return (
    <DataBridgeSectionCard
      title={getSectionTitle()}
      description={getSectionDescription()}
    >
      <DataBridgeKeyValue
        label="Service"
        value={destinationKind}
        isLoading={isLoading}
        formatValue={(kind) => upperFirst(kind as string)}
      />

      {renderDestinationDetails()}
    </DataBridgeSectionCard>
  )
}
